import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { useToast } from '@/shared/hooks';
import { templateMetadataSchema } from '../schemas';
import type { TemplateMetadataFormValues } from '../schemas';
import { useUpdateProjectTemplateMetadata } from '../hooks/useProjectTemplateMutations';
import { TEMPLATE_SECTOR } from '../types';
import type { TemplateGalleryItem } from '../types';

interface TemplateMetadataDialogProps {
  template: TemplateGalleryItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Edits title/sector/description of a tenant-owned template — structure edits happen in `TemplatePreviewPanel`. */
export function TemplateMetadataDialog({
  template,
  open,
  onOpenChange,
}: TemplateMetadataDialogProps) {
  const { t } = useTranslation('projectTemplates');
  const { toast } = useToast();
  const updateMetadata = useUpdateProjectTemplateMetadata(template.id);

  const { control, handleSubmit, reset } = useForm<TemplateMetadataFormValues>({
    resolver: zodResolver(templateMetadataSchema),
    defaultValues: {
      title: template.title,
      sector: template.sector,
      description: template.description ?? '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        title: template.title,
        sector: template.sector,
        description: template.description ?? '',
      });
    }
  }, [open, template, reset]);

  const sectorOptions = Object.values(TEMPLATE_SECTOR).map((sector) => ({
    value: sector,
    label: t(`gallery.sectorFilter.${sector}`),
  }));

  function onSubmit(values: TemplateMetadataFormValues) {
    updateMetadata.mutate(values, {
      onSuccess: () => {
        toast.success({ title: t('toast.updated') });
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full rounded-none md:max-w-[480px] md:rounded-lg"
        data-testid="project-templates-metadata-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('metadataDialog.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('metadataDialog.title')}</DialogDescription>
        </DialogHeader>

        <form
          id="project-template-metadata-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <FormFieldInput
            control={control}
            name="title"
            label={t('editor.fields.name')}
            floatingLabel
            disabled={updateMetadata.isPending}
            data-testid="project-templates-metadata-name-input"
          />
          <FormFieldSelect
            control={control}
            name="sector"
            label={t('editor.fields.sector')}
            floatingLabel
            options={sectorOptions}
            disabled={updateMetadata.isPending}
            data-testid="project-templates-metadata-sector-select"
          />
          <FormFieldTextarea
            control={control}
            name="description"
            label={t('editor.fields.description')}
            floatingLabel
            disabled={updateMetadata.isPending}
            data-testid="project-templates-metadata-description-input"
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMetadata.isPending}
          >
            {t('metadataDialog.cancel')}
          </Button>
          <Button
            type="submit"
            form="project-template-metadata-form"
            disabled={updateMetadata.isPending}
            data-testid="project-templates-metadata-save-button"
          >
            {updateMetadata.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('metadataDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
